import { useId, useState, type ReactNode } from 'react'
import { recordListPageSizes, type RecordListPageSize } from '../pagination/pageSizePreference'
import { useLocale } from '../i18n/LocaleContext'


export type PagedListControlsProps = {
  applyLabel: string
  clearLabel: string
  rowsPerPageLabel: string
  previousLabel: string
  nextLabel: string
  pageLabel: string
  totalLabel: string
  filters: ReactNode
  page: number
  pageSize: RecordListPageSize
  totalCount: number
  loading?: boolean
  onApply: () => void
  onClear: () => void
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: RecordListPageSize) => void
  formatNumber: (value: number) => string
}

/** Shared controls for operational record lists. Data is always paged by the API. */
export function PagedListControls({ applyLabel, clearLabel, rowsPerPageLabel,
  previousLabel, nextLabel, pageLabel, totalLabel, filters, page, pageSize,
  totalCount, loading = false, onApply, onClear, onPageChange, onPageSizeChange,
  formatNumber }: PagedListControlsProps) {
  const { t } = useLocale()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filtersId = useId()
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  return <>
    <button className="secondary-button record-list-filter-toggle" type="button"
      aria-controls={filtersId} aria-expanded={filtersOpen}
      onClick={() => setFiltersOpen(current => !current)}>
      <span aria-hidden="true">⌕</span><span>{t('recordList.filters')}</span>
    </button>
    <form id={filtersId} className={`record-list-filters ${filtersOpen ? 'is-open' : ''}`}
      onSubmit={event => { event.preventDefault(); onApply(); setFiltersOpen(false) }}>
      <div className="record-list-filter-fields">{filters}</div>
      <div className="record-list-filter-actions">
        <button className="primary-button" type="submit" disabled={loading}>{applyLabel}</button>
        <button className="secondary-button" type="button" disabled={loading} onClick={onClear}>{clearLabel}</button>
      </div>
    </form>
    <footer className="table-pagination">
      <span className="record-list-pagination-summary">
        <span className="desktop-pagination-summary">
          {pageLabel.replace('{page}', formatNumber(page)).replace('{total}', formatNumber(totalPages))}
          {' · '}{totalLabel}: {formatNumber(totalCount)}
        </span>
        <span className="mobile-pagination-summary" aria-label={pageLabel.replace('{page}', formatNumber(page)).replace('{total}', formatNumber(totalPages))}>
          {formatNumber(page)} / {formatNumber(totalPages)}
        </span>
      </span>
      <label className="page-size-control"><span>{rowsPerPageLabel}</span>
        <select aria-label={rowsPerPageLabel} value={pageSize} disabled={loading} onChange={event =>
          onPageSizeChange(Number(event.target.value) as RecordListPageSize)}>
          {recordListPageSizes.map(size => <option key={size} value={size}>{formatNumber(size)}</option>)}
        </select>
      </label>
      <div className="record-list-pagination-actions"><button className="secondary-button" type="button"
        aria-label={previousLabel} disabled={loading || page <= 1} onClick={() => onPageChange(page - 1)}><span className="responsive-action-icon" aria-hidden="true">←</span><span className="responsive-action-label">{previousLabel}</span></button>
      <button className="secondary-button" type="button" disabled={loading || page >= totalPages}
        aria-label={nextLabel} onClick={() => onPageChange(page + 1)}><span className="responsive-action-label">{nextLabel}</span><span className="responsive-action-icon" aria-hidden="true">→</span></button></div>
    </footer>
  </>
}
